import { createContext, useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/auth.service.js';
import { authAPI } from '../utils/api.js';
import { APP, STORAGE_KEYS } from '../config/app';
import { clearRoomAccessCache } from '../hooks/roomAccessCache';
import { clearSavedBaskets } from '../portals/student/bookDeskBasket';
import { clearSavedCarts } from '../hooks/bookingCartStorage';
import { setSignInNotice } from '../utils/signInNotice';
import { SESSION_ENDED_EVENT, sessionEndedNotice } from '../utils/sessionEnd';
import {
  MASTER_SNAPSHOT_KEY,
  MASTER_TOKEN_KEY,
  clearImpersonationStash,
  readImpersonation,
} from './impersonation';

const AuthContext = createContext(null);

/**
 * What one person left in this browser for their next booking — the desk
 * basket and the header cart. Dropped whenever the person changes (sign-out,
 * View As, impersonation), so a shared machine never hands it to the next.
 */
function forgetHeldBookings() {
  clearSavedBaskets();
  clearSavedCarts();
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewAsRole, setViewAsRole] = useState(() => sessionStorage.getItem('viewAsRole') || null);
  // View As state, read from the token in use — never from the stash alone
  // (see ./impersonation.js).
  const [viewingAs, setViewingAs] = useState(() => readImpersonation());
  const impersonating = viewingAs.impersonating;
  const masterAdminSnapshot = viewingAs.snapshot;

  useEffect(() => {
    const loadUser = async () => {
      // Check if user explicitly logged out - if so, don't auto-login
      const explicitlyLoggedOut = localStorage.getItem(STORAGE_KEYS.LOGGED_OUT) === 'true';

      if (explicitlyLoggedOut) {
        setLoading(false);
        return;
      }

      try {
        const currentUser = await authService.getCurrentUser();
        if (currentUser) {
          setUser(currentUser);
        }
        // No cached user means stay logged out (show login page)
      } catch (error) {
        console.error('Failed to load user:', error);
        // Auth error means stay logged out
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, []);

  // The server refused this tab's session mid-use (suspended, or the password
  // or email changed elsewhere): sign out properly and tell the sign-in page
  // why, rather than leave every screen showing raw API errors. The first
  // render's `logout` is enough: it only clears storage and navigates.
  useEffect(() => {
    const onSessionEnded = (event) => {
      setSignInNotice(sessionEndedNotice(event?.detail?.message));
      logout();
    };
    window.addEventListener(SESSION_ENDED_EVENT, onSessionEnded);
    return () => window.removeEventListener(SESSION_ENDED_EVENT, onSessionEnded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    // Clear logout flag when logging in
    localStorage.removeItem(STORAGE_KEYS.LOGGED_OUT);
    const loggedInUser = await authService.login(email, password);
    // A fresh sign-in is nobody's View As: whatever a previous session parked
    // in this tab is gone.
    clearImpersonationStash();
    setViewingAs(readImpersonation());
    setUser(loggedInUser);
    return loggedInUser;
  };

  /**
   * Biometric sign-in, in two steps, because the order matters.
   *
   * `exchangeDeviceToken` trades the phone's device token for a session and the
   * ROTATED device token, and deliberately does NOT set the user. Setting the
   * user is what makes the login screen redirect and unmount, and the token just
   * presented is already dead — so the caller must put its replacement in the
   * secure store first, and only then call `completeDeviceSignIn`.
   */
  const exchangeDeviceToken = (deviceToken) => authService.loginWithDeviceToken(deviceToken);

  /** Ends in exactly the state `login` does: flag cleared, user set. */
  const completeDeviceSignIn = async (signedInUser) => {
    localStorage.removeItem(STORAGE_KEYS.LOGGED_OUT);
    clearImpersonationStash();
    setViewingAs(readImpersonation());
    setUser(signedInUser);
    return signedInUser;
  };

  const logout = async () => {
    // Set flag to prevent auto-login
    localStorage.setItem(STORAGE_KEYS.LOGGED_OUT, 'true');
    sessionStorage.removeItem('viewAsRole');
    // The master's parked session must never outlive Sign Out: the next person
    // to sign in on this tab could otherwise "exit" View As into it.
    clearImpersonationStash();
    // Answers held about the person signing out, so the next person to sign in
    // in this tab is never shown what the last one was allowed.
    clearRoomAccessCache();
    // …and the desk basket and cart they left here (shared machines).
    forgetHeldBookings();
    await authService.logout();
    setUser(null);
    // Navigate to home page (login)
    window.location.href = `${APP.basePath}/`;
  };

  const startViewAs = (role) => {
    forgetHeldBookings();
    sessionStorage.setItem('viewAsRole', role);
    setViewAsRole(role);
  };

  const stopViewAs = () => {
    forgetHeldBookings();
    sessionStorage.removeItem('viewAsRole');
    setViewAsRole(null);
  };

  const startImpersonation = async (targetUserId) => {
    try {
      const currentToken = localStorage.getItem(STORAGE_KEYS.TOKEN);
      const res = await authAPI.impersonate(targetUserId);
      // Save master admin state so we can restore it
      sessionStorage.setItem(MASTER_TOKEN_KEY, currentToken);
      sessionStorage.setItem(MASTER_SNAPSHOT_KEY, JSON.stringify(user));
      // Switch to impersonated user's token
      localStorage.setItem(STORAGE_KEYS.TOKEN, res.token);
      // Clear any viewAs state — impersonation supersedes it
      forgetHeldBookings();
      sessionStorage.removeItem('viewAsRole');
      setViewAsRole(null);
      setViewingAs(readImpersonation());
      setUser(res.user);
    } catch (err) {
      console.error('Impersonation failed:', err);
      throw err;
    }
  };

  const stopImpersonation = () => {
    const { snapshot, masterToken } = readImpersonation();
    // No trusted way back (a second tab, or a stash that does not match the
    // token): ending View As here means signing out.
    if (!snapshot || !masterToken) {
      logout();
      return;
    }
    localStorage.setItem(STORAGE_KEYS.TOKEN, masterToken);
    clearImpersonationStash();
    forgetHeldBookings();
    setUser(snapshot);
    setViewingAs(readImpersonation());
    // Return to master admin portal
    window.location.href = `${APP.basePath}/admin`;
  };

  const isAdmin = user?.role === 'master_admin' || user?.role === 'department_admin' || user?.role === 'head_of_department';

  const value = {
    user,
    setUser,
    loading,
    login,
    exchangeDeviceToken,
    completeDeviceSignIn,
    logout,
    isAuthenticated: !!user,
    viewAsRole,
    startViewAs: isAdmin ? startViewAs : undefined,
    stopViewAs,
    isViewingAs: !!viewAsRole,
    impersonating,
    masterAdminSnapshot,
    startImpersonation: user?.role === 'master_admin' ? startImpersonation : undefined,
    stopImpersonation: impersonating ? stopImpersonation : undefined,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
